import { useQuery } from '@apollo/client';
import React, { useState } from 'react';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  Stack,
  Text,
} from '@eluve/components';

import { getLlmModelArgsForOverrideQuery } from '../operations';

import { OverrideSection } from './OverrideSection';
import { VariantConfig } from './types';

interface PromptModelArgOverrideProps {
  config: VariantConfig;
  onChange: (updates: Partial<VariantConfig>) => void;
}

export const PromptModelArgOverride: React.FC<PromptModelArgOverrideProps> = ({
  config,
  onChange,
}) => {
  const { data } = useQuery(getLlmModelArgsForOverrideQuery);

  const hasValues = config.PROMPT_MODEL_ARG !== undefined;
  const [isExpanded, setIsExpanded] = useState(hasValues);

  const modelArgs = data?.llmModelArgs ?? [];

  const selectedModelArg = modelArgs.find(
    (m) => m.id === config.PROMPT_MODEL_ARG,
  );

  const handleEnabledChange = (enabled: boolean) => {
    setIsExpanded(enabled);
    if (!enabled) {
      onChange({ PROMPT_MODEL_ARG: undefined });
    }
  };

  return (
    <OverrideSection
      label="Override Prompt Model"
      enabled={isExpanded}
      onEnabledChange={handleEnabledChange}
    >
      <Stack gap={1}>
        <Text size="xs" color="secondary">
          Model Args
        </Text>
        <Combobox>
          <ComboboxSelectButton className="w-full">
            {selectedModelArg
              ? `${selectedModelArg.modelType}${selectedModelArg.name ? ` - ${selectedModelArg.name}` : ''}`
              : 'Select model args'}
          </ComboboxSelectButton>
          <ComboboxDropdown searchPlaceholder="Search models...">
            <ComboboxEmpty>No models found</ComboboxEmpty>
            {modelArgs.map((arg) => (
              <ComboboxOption
                key={arg.id}
                onSelect={() => onChange({ PROMPT_MODEL_ARG: arg.id })}
                searchValue={`${arg.modelType} ${arg.name ?? ''} ${arg.id}`}
              >
                <Stack justify="between" gap={2} direction="horizontal">
                  <Text size="md" inline>
                    {arg.modelType}
                    {arg.name ? ` - ${arg.name}` : ''}
                  </Text>
                  <Text inline size="xs" className="font-mono" color="tertiary">
                    {arg.id.slice(0, 8)}
                  </Text>
                </Stack>
              </ComboboxOption>
            ))}
          </ComboboxDropdown>
        </Combobox>
      </Stack>
    </OverrideSection>
  );
};
